import React, { useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { resolvePersonImageUrl } from '../../utils/resolvePersonImageUrl';
import { formatAccreditationCategoryLabel } from '../../utils/invitationTypeFormioUtils';
import '../css/AssignParticipantsModal.css';

const participantName = (person) =>
  [person?.name, person?.mname, person?.lname].filter(Boolean).join(' ') || 'Unnamed participant';

const toOptions = (items, getLabel) =>
  (items || []).map((item) => ({
    value: item?._id || '',
    label: getLabel(item) || 'Unnamed',
  }));

const findOption = (options, value) => options.find((option) => String(option.value) === String(value)) || null;

const assignSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    borderColor: state.isFocused ? '#a67c2c' : '#d9cfbf',
    borderWidth: 1.5,
    boxShadow: state.isFocused ? '0 0 0 3px rgba(166, 124, 44, 0.22)' : 'none',
    backgroundColor: '#fffefb',
    cursor: 'pointer',
    '&:hover': {
      borderColor: '#c4a86a',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '4px 12px',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#8a7d68',
    fontWeight: 500,
    fontSize: 14,
  }),
  singleValue: (base) => ({
    ...base,
    color: '#2a2418',
    fontWeight: 600,
    fontSize: 14,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? '#a67c2c' : '#8a7345',
    padding: '0 12px',
    '&:hover': {
      color: '#a67c2c',
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
    overflow: 'hidden',
    border: '1px solid #e0d4bd',
    borderRadius: 12,
    backgroundColor: '#fffefb',
    boxShadow: '0 14px 32px rgba(45, 35, 18, 0.16)',
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 220,
    padding: 6,
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: 8,
    margin: '2px 0',
    padding: '10px 12px',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: state.isSelected ? 700 : 600,
    color: state.isSelected || state.isFocused ? '#2a2418' : '#3d3628',
    backgroundColor: state.isSelected
      ? 'rgba(166, 124, 44, 0.18)'
      : state.isFocused
        ? 'rgba(166, 124, 44, 0.08)'
        : 'transparent',
    ':active': {
      backgroundColor: 'rgba(166, 124, 44, 0.22)',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: '#6b5d48',
    fontSize: 13,
    fontWeight: 600,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 1100,
  }),
};

const AssignSelect = ({ label, required, options, value, onChange, placeholder }) => (
  <label className="assign-modal-field">
    <span className="assign-modal-field__label">
      {label}
      {required ? <strong className="assign-modal-field__required" aria-hidden="true"> *</strong> : null}
    </span>
    <Select
      classNamePrefix="assign-select"
      styles={assignSelectStyles}
      options={options}
      value={findOption(options, value)}
      onChange={(option) => onChange(option?.value || '')}
      placeholder={placeholder}
      isClearable={!required}
      isSearchable
      menuPlacement="auto"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
    />
  </label>
);

const AssignParticipantsModal = ({
  apiUrl,
  competitionId,
  selectedParticipants,
  groups,
  emsRoles,
  invitationTypes,
  accreditationCategories,
  onRemoveParticipant,
  onClose,
  onAssigned,
  onSubmitAssignment,
}) => {
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedEmsRole, setSelectedEmsRole] = useState('');
  const [selectedInvitationType, setSelectedInvitationType] = useState('');
  const [selectedAccreditationCategory, setSelectedAccreditationCategory] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const groupOptions = useMemo(() => toOptions(groups, (group) => group?.group_name), [groups]);
  const roleOptions = useMemo(() => toOptions(emsRoles, (role) => role?.role_name), [emsRoles]);
  const invitationOptions = useMemo(
    () => toOptions(invitationTypes, (type) => type?.name),
    [invitationTypes]
  );
  const accreditationOptions = useMemo(
    () => toOptions(accreditationCategories, formatAccreditationCategoryLabel),
    [accreditationCategories]
  );

  // Ignore close while a request is in flight so a second submit can't start behind it.
  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleAssign = async () => {
    if (!selectedGroup) {
      setError('Select a group before assigning participants.');
      return;
    }

    // New-person mode (Add Person form): hand the values back, the caller creates the person.
    if (onSubmitAssignment) {
      setError('');
      setIsSubmitting(true);
      try {
        await onSubmitAssignment({
          group_id: selectedGroup,
          role_id: selectedEmsRole || null,
          invitation_type_id: selectedInvitationType || null,
          accreditation_category_id: selectedAccreditationCategory || null,
        });
      } catch (err) {
        console.error('Failed to assign participant:', err);
        setError(err?.response?.data?.message || 'Unable to assign participant. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const personIds = selectedParticipants.map((person) => person?._id).filter(Boolean);
    if (!personIds.length) {
      setError('Select at least one participant.');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${apiUrl}/ems/participants/bulk-add`, {
        person_ids: personIds,
        competition_id: competitionId,
        group_id: selectedGroup,
        invitation_type_id: selectedInvitationType || null,
        role_id: selectedEmsRole || null,
        accreditation_category_id: selectedAccreditationCategory || null,
      });
      const createdParticipants = Array.isArray(response?.data?.participants)
        ? response.data.participants
        : [];
      await onAssigned(createdParticipants);
    } catch (err) {
      console.error('Failed to assign participants:', err);
      setError(err?.response?.data?.message || 'Unable to assign participants. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="assign-modal-overlay" role="presentation" onMouseDown={handleClose}>
      <section
        className="assign-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="assign-modal-header">
          <div>
            <h2 id="assign-modal-title" className="assign-modal-title">Assign to Competition</h2>
            <p className="assign-modal-subtitle">Set the details for the selected participants.</p>
          </div>
          <button type="button" className="assign-modal-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </header>

        {error && <div className="assign-modal-error" role="alert">{error}</div>}

        <section className="assign-modal-section" aria-labelledby="assignment-details-title">
          <h3 id="assignment-details-title" className="assign-modal-section-title">
            Assignment Details
          </h3>
          <div className="assign-modal-fields">
            <AssignSelect
              label="Group"
              required
              options={groupOptions}
              value={selectedGroup}
              onChange={setSelectedGroup}
              placeholder="Select group"
            />
            <AssignSelect
              label="Role"
              options={roleOptions}
              value={selectedEmsRole}
              onChange={setSelectedEmsRole}
              placeholder="Select role"
            />
            <AssignSelect
              label="Invitation Type"
              options={invitationOptions}
              value={selectedInvitationType}
              onChange={setSelectedInvitationType}
              placeholder="Select invitation type"
            />
            <AssignSelect
              label="Accreditation Category"
              options={accreditationOptions}
              value={selectedAccreditationCategory}
              onChange={setSelectedAccreditationCategory}
              placeholder="Select accreditation category"
            />
          </div>
        </section>

        <section className="assign-modal-selected" aria-label="Selected participants">
          <h3>Selected Participants ({selectedParticipants.length})</h3>
          <div className="assign-modal-selected-list">
            {selectedParticipants.map((person, index) => (
              <div key={person?._id || index} className="assign-modal-person">
                <img
                  src={resolvePersonImageUrl(person?.image, apiUrl) || '/images/avatar.png'}
                  alt=""
                  className="assign-modal-person-image"
                />
                <span className="assign-modal-person-name">{participantName(person)}</span>
                <span className="assign-modal-person-country">{person?.country_id?.code || '—'}</span>
                {onRemoveParticipant && (
                  <button
                    type="button"
                    className="assign-modal-remove"
                    onClick={() => onRemoveParticipant(person?._id)}
                    aria-label={`Remove ${participantName(person)}`}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>

        <footer className="assign-modal-footer">
          <button type="button" className="btn assign-modal-cancel" onClick={handleClose}>Cancel</button>
          <button
            type="button"
            className="btn assign-modal-submit"
            onClick={handleAssign}
            disabled={!selectedGroup || !selectedParticipants.length || isSubmitting}
          >
            {isSubmitting ? 'Assigning…' : 'Assign to Competition'}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default AssignParticipantsModal;
